/** ISBNs are written with and without hyphens or spaces; compared by their digits (and check character) only. */
export const normalizeIsbn = (isbn: string): string => isbn.replace(/[^\dX]/gi, "").toUpperCase();

/** Nothing but ISBN characters, maybe after a pasted "ISBN" label, so "Ausgabe 2022" stays a title search. */
export const ISBN_QUERY_PATTERN = /^(isbn[\s:-]*)?[\d\s-]+x?$/i;

/** A whole ISBN-13 or ISBN-10, written any way; 10 digits starting 978/979 are an unfinished ISBN-13. */
export const isCompleteIsbn = (value: string): boolean => {
    const digits = normalizeIsbn(value);
    return (
        ISBN_QUERY_PATTERN.test(value.trim()) &&
        (digits.length === 13 || (digits.length === 10 && !/^97[89]/.test(digits)))
    );
};

const getIsbn13CheckDigit = (first12Digits: string): string => {
    const sum = first12Digits
        .split("")
        .reduce((total, digit, index) => total + Number(digit) * (index % 2 === 0 ? 1 : 3), 0);
    return String((10 - (sum % 10)) % 10);
};

const getIsbn10CheckCharacter = (first9Digits: string): string => {
    const sum = first9Digits.split("").reduce((total, digit, index) => total + Number(digit) * (10 - index), 0);
    const check = (11 - (sum % 11)) % 11;
    return check === 10 ? "X" : String(check);
};

const isValidIsbn13 = (digits: string): boolean =>
    /^97[89]\d{10}$/.test(digits) && getIsbn13CheckDigit(digits.slice(0, 12)) === digits[12];

/** The ISBN-13 an ISBN stands for: an ISBN-13 as written, an ISBN-10 converted. Undefined for anything else. */
export const toIsbn13 = (isbn: string): string | undefined => {
    const digits = normalizeIsbn(isbn);
    if (isValidIsbn13(digits)) {
        return isbn.trim();
    }
    if (/^\d{9}[\dX]$/.test(digits) && getIsbn10CheckCharacter(digits.slice(0, 9)) === digits[9]) {
        const first12Digits = `978${digits.slice(0, 9)}`;
        return first12Digits + getIsbn13CheckDigit(first12Digits);
    }
    return undefined;
};

/**
 * The valid ISBN-13s, each once. ISBN-10s are dropped: the shop lists real ones as ISBN-13 too, and catalogue
 * numbers like "1100031715" pass the ISBN-10 check.
 */
export const getValidIsbn13s = (isbns: Array<string>): Array<string> => {
    const seenDigits = new Set<string>();
    return isbns
        .map((isbn) => isbn.trim())
        .filter((isbn) => {
            const digits = normalizeIsbn(isbn);
            if (!isValidIsbn13(digits) || seenDigits.has(digits)) {
                return false;
            }
            seenDigits.add(digits);
            return true;
        });
};
